import { usePatchFileRecordRegion } from "@/api/fileRecords";
import {
  useHtmlIslandAutosave,
  type BlockToParentMessage,
  type HtmlIslandAutosaveState,
} from "@cohesive/ui-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * How long content stays frozen after the last successful save while no
 * session is active.
 *
 * A save triggers a server event, which invalidates the record, which
 * refetches the body. That refetch carries the content the user is already
 * looking at, but the card can't know that without replaying the edit
 * locally — so instead of remounting the iframe the moment a session ends, we
 * hold the freeze briefly and let the refetch land in the stash, where the
 * unfreeze applies it exactly once.
 *
 * Same class of heuristic (and roughly the same duration) as the detail
 * pane's post-save SSE suppression.
 */
const POST_SAVE_SETTLE_MS = 3_000;

export interface UseHtmlBlockEditSessionParams {
  fileRecordId?: string;
  /** The block-focus latch currently holds this node in `edit` mode. */
  editing: boolean;
  /** Presigned → public reversal, applied to every island string. */
  reversePresignedUrls: (html: string) => string;
}

export interface UseHtmlBlockEditSessionResult {
  /** Hold refetches out of the iframe (typing, saving, or settling). */
  contentFrozen: boolean;
  saveState: HtmlIslandAutosaveState;
  /** Route a runtime message from the block into the autosave engine. */
  onIslandMessage: (msg: BlockToParentMessage) => void;
  /** Save pending islands now — called on the focus latch's falling edge. */
  flush: () => void;
}

const IDLE_STATE: HtmlIslandAutosaveState = {
  pendingCount: 0,
  isSaving: false,
};

/**
 * In-place island editing for a canvas HTML block.
 *
 * The block's iframe is opaque-origin, so unlike the detail pane the parent
 * cannot read island content out of the DOM. The injected runtime posts it
 * instead, and this hook keeps the latest value per region in a map that
 * backs the autosave engine's `readIslandContent`.
 *
 * Everything about WHEN a session ends lives in the focus latch; this hook
 * only reacts to it. That is why there is a single `flush`, called from the
 * latch's falling edge, rather than a flush at each of the eight exit paths.
 */
export function useHtmlBlockEditSession({
  fileRecordId,
  editing,
  reversePresignedUrls,
}: UseHtmlBlockEditSessionParams): UseHtmlBlockEditSessionResult {
  const { mutateAsync: patchRegions } = usePatchFileRecordRegion(
    fileRecordId ?? "",
  );

  // regionId → latest innerHTML reported by the in-frame runtime.
  const islandStoreRef = useRef(new Map<string, string>());
  const [saveState, setSaveState] =
    useState<HtmlIslandAutosaveState>(IDLE_STATE);
  const [settling, setSettling] = useState(false);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const readIslandContent = useCallback(
    (editId: string) => islandStoreRef.current.get(editId) ?? null,
    [],
  );

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const editingRef = useRef(editing);
  editingRef.current = editing;

  const onSaveSuccess = useCallback(() => {
    // Only arm the settle window once the user has left the session — while
    // they're still typing, `editing` already holds the freeze.
    if (editingRef.current) return;
    clearSettleTimer();
    setSettling(true);
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      setSettling(false);
    }, POST_SAVE_SETTLE_MS);
  }, [clearSettleTimer]);

  const {
    handleIslandFocus,
    handleIslandInput,
    handleIslandFocusOut,
    flushDirtyRegions,
    resetAutosave,
  } = useHtmlIslandAutosave({
    readIslandContent,
    patchRegions,
    onSaveSuccess,
    sanitizeContent: reversePresignedUrls,
    onAutosaveState: setSaveState,
  });

  // Starting a session begins from a clean slate: island content is whatever
  // the freshly-configured runtime reports, not what a previous session left.
  useEffect(() => {
    if (!editing) return;
    islandStoreRef.current.clear();
    resetAutosave();
  }, [editing, resetAutosave]);

  const onIslandMessage = useCallback(
    (msg: BlockToParentMessage) => {
      switch (msg.type) {
        case "island-focus":
          islandStoreRef.current.set(msg.regionId, msg.html);
          handleIslandFocus(msg.regionId, msg.html);
          break;
        case "island-input":
          islandStoreRef.current.set(msg.regionId, msg.html);
          handleIslandInput(msg.regionId, msg.html);
          break;
        case "island-blur":
          islandStoreRef.current.set(msg.regionId, msg.html);
          handleIslandFocusOut(msg.regionId, msg.html);
          break;
        case "flush-request":
          // Cmd/Ctrl+S inside the block. The runtime drains its debounced
          // input before posting this, so the store is already current.
          flushDirtyRegions();
          break;
        default:
          break;
      }
    },
    [
      handleIslandFocus,
      handleIslandInput,
      handleIslandFocusOut,
      flushDirtyRegions,
    ],
  );

  const flush = useCallback(() => {
    flushDirtyRegions();
  }, [flushDirtyRegions]);

  useEffect(() => clearSettleTimer, [clearSettleTimer]);

  const contentFrozen =
    editing || saveState.pendingCount > 0 || saveState.isSaving || settling;

  return useMemo(
    () => ({ contentFrozen, saveState, onIslandMessage, flush }),
    [contentFrozen, saveState, onIslandMessage, flush],
  );
}
